import axios from "../../../api/axios";
import { toast } from "react-toastify";

export const INVOICE_ENDPOINTS = {
  INVOICES_BASE: "/v2/invoices/invoice",
  INVOICE_BY_ID: "/v2/invoices/invoice/:id",
  INVOICE_ARCHIVE: "/v2/invoices/invoice/archive",
  INVOICE_ARCHIVE_RESTORE: "/v2/invoices/invoice/archive/restore",
  INVOICE_ARCHIVE_BY_ID: "/v2/invoices/invoice/archive/:id",
  INVOICE_DOCUMENT: "/v2/invoices/invoice/:id/document",
  INVOICE_STATUS_COUNTS: "/v2/invoices/invoice/status-counts",
  INVOICE_REVIEW_QUEUE: "/v2/invoices/invoice/review-queue",
  INVOICE_REVIEW: "/v2/invoices/invoice/:id/review",
  INVOICE_ELIGIBILITY: "/v2/invoices/invoice/eligibility",
  INVOICE_ELIGIBLE_FOR_SOA: "/v2/invoices/invoice/eligible-for-soa",
  INVOICE_NEXT_NO: "/v2/invoices/invoice/next-no",
  INVOICE_GROUP: "/v2/invoices/invoice/group/:groupId",
  INVOICE_GROUP_LINES: "/v2/invoices/invoice/group/:groupId/lines",
  INVOICE_APPROVE: "/v2/invoices/invoice/:id/approve",
  INVOICE_DECLINE: "/v2/invoices/invoice/:id/decline",
  INVOICE_MARK_PAID: "/v2/invoices/invoice/:id/mark-paid",
  INVOICE_ENTERED_ACCT: "/v2/invoices/invoice/:id/entered-acct",
  INVOICE_BULK_MARK_PAID: "/v2/invoices/invoice/bulk-mark-paid",
  INVOICE_BULK_APPROVE: "/v2/invoices/invoice/bulk-approve",
  INVOICE_STATUS: "/v2/invoices/invoice/:id/status",
  INVOICE_COMMENTS: "/v2/invoices/invoice/:id/comments",
  INVOICE_SIGNED_URLS: "/v2/invoices/invoice/signed-urls",
  INVOICE_UPLOAD_REQUEST: "/v2/invoices/invoice/:id/attachments/request-upload",
  INVOICE_CONFIRM_UPLOAD: "/v2/invoices/invoice/:id/attachments/confirm",
  INVOICE_REMOVE_ATTACHMENT: "/v2/invoices/invoice/:id/attachments/remove",
  INVOICE_AI_EXTRACT: "/v2/invoices/invoice/ai/extract",
  INVOICE_STAGING_UPLOAD_REQUEST: "/v2/invoices/invoice/attachments/staging/request-upload",
  INVOICE_STAGING_SIGNED_URLS: "/v2/invoices/invoice/attachments/staging/signed-urls",
  INVOICE_STAGING_REMOVE: "/v2/invoices/invoice/attachments/staging/remove",
  INVOICE_FORWARD: "/v2/invoices/invoice/:id/forward",

  CATEGORIES_BASE: "/v2/invoices/category",
  CATEGORY_BY_ID: "/v2/invoices/category/:id",

  VENDORS_BASE: "/v2/invoices/vendor",
  VENDOR_BY_ID: "/v2/invoices/vendor/:id",

  AUDITORS_BASE: "/v2/invoices/auditor",
  AUDITOR_BY_ID: "/v2/invoices/auditor/:id",

  SETTINGS_BASE: "/v2/invoices/settings",

  PAYROLL_BASE: "/v2/invoices/payroll",
  PAYROLL_BY_ID: "/v2/invoices/payroll/:id",
  PAYROLL_SEED: "/v2/invoices/payroll/seed-defaults",
};

const handleApiError = (error, defaultMessage) => {
  const message =
    error.response?.data?.message || error.message || defaultMessage || "Unexpected error";
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

// ── Invoices ─────────────────────────────────────────────────────────────
export const getInvoices = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICES_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoices"); }
};

export const getInvoiceById = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_BY_ID.replace(":id", id);
    const res = await axios.get(url);
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice"); }
};

export const getInvoiceStatusCounts = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_STATUS_COUNTS, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice status counts"); }
};

// ── Vendor-submission review queue ─────────────────────────────────────────
export const getInvoiceReviewQueue = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_REVIEW_QUEUE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch review queue"); }
};

export const reviewInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_REVIEW.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to submit review"); }
};

// ── Approval workflow ───────────────────────────────────────────────────────
// Tells the current user whether they may generate an invoice, and (for vendors)
// which properties/buildings they're allowed to invoice.
export const getInvoiceEligibility = async () => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_ELIGIBILITY);
    return res.data?.data || { canCreate: false, properties: [] };
  } catch (e) {
    handleApiError(e, "Failed to check invoice access");
    return { canCreate: false, properties: [] };
  }
};

// Technical Lead (or company) approves a pending invoice.
export const approveInvoice = async (id, payload = {}) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_APPROVE.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to approve invoice"); }
};

// Technical Lead (or company) declines a pending invoice (reason required).
export const declineInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_DECLINE.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to decline invoice"); }
};

// Final Auditor (or company) settles an approved invoice.
export const markInvoicePaid = async (id, payload = {}) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_MARK_PAID.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to mark invoice paid"); }
};

export const setInvoiceEnteredAcct = async (id, entered) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_ENTERED_ACCT.replace(":id", id);
    const res = await axios.patch(url, { entered }, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update accounting status"); }
};

export const bulkMarkInvoicesPaid = async (ids) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_BULK_MARK_PAID, { ids }, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to mark invoices paid"); }
};

// Technical Lead (or company) approves many pending invoices at once.
export const bulkApproveInvoices = async (ids, note = "") => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_BULK_APPROVE, { ids, note }, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to approve invoices"); }
};

export const createInvoice = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICES_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create invoice"); }
};

export const updateInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update invoice"); }
};

// Save the whole invoice from the edit form. `payload.lines` is every line to
// keep; a line with `_id` updates that document, one without is created, and
// any existing line not sent is deleted. One request, one notification.
export const saveInvoiceGroupLines = async (groupId, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_GROUP_LINES.replace(":groupId", groupId);
    const res = await axios.put(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) {
    // The server answers 404 "Invoice not found" when the caller cannot see
    // every line of the invoice (a partial viewer must not edit it, and must
    // not learn more). Any other message is shown as-is.
    if (e?.response?.status === 404 && e?.response?.data?.message === "Invoice not found") {
      toast.error("You can only edit an invoice you can see in full.");
      throw new Error("Invoice not fully visible");
    }
    handleApiError(e, "Failed to update invoice");
  }
};

export const updateInvoiceStatus = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_STATUS.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update invoice status"); }
};

export const addInvoiceComment = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_COMMENTS.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to add comment"); }
};

// ── Archive (replaces delete) ─────────────────────────────────────────────
export const archiveInvoiceLines = async (invoiceIds, reason) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_ARCHIVE, { invoiceIds, reason });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to archive invoice"); }
};

export const getArchivedInvoices = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_ARCHIVE, { params });
    return res.data?.data || [];
  } catch (e) { handleApiError(e, "Failed to load archived invoices"); }
};

export const getArchivedInvoice = async (id) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_ARCHIVE_BY_ID.replace(":id", id));
    return res.data?.data || null;
  } catch (e) { handleApiError(e, "Failed to load the archived invoice"); }
};

export const restoreArchivedInvoice = async (invoiceIds) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_ARCHIVE_RESTORE, { invoiceIds });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to restore invoice"); }
};

export const forwardInvoice = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_FORWARD.replace(":id", id);
    const res = await axios.post(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to forward invoice"); }
};

// ── Attachments ───────────────────────────────────────────────────────────
export const requestInvoiceUploadUrl = async (invoiceId, fileName, contentType) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_UPLOAD_REQUEST.replace(":id", invoiceId);
    const res = await axios.post(url, { fileName, contentType });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to obtain upload URL"); }
};

export const confirmInvoiceUpload = async (invoiceId, key) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_CONFIRM_UPLOAD.replace(":id", invoiceId);
    const res = await axios.post(url, { key });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to confirm upload"); }
};

export const removeInvoiceAttachment = async (invoiceId, key) => {
  try {
    const url = INVOICE_ENDPOINTS.INVOICE_REMOVE_ATTACHMENT.replace(":id", invoiceId);
    const res = await axios.post(url, { key });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to remove attachment"); }
};

export const getInvoiceSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_SIGNED_URLS, { keys });
    return res.data.data;
  } catch (e) {
    handleApiError(e, "Failed to generate signed URLs");
    return {};
  }
};

// ── Categories ────────────────────────────────────────────────────────────
export const getInvoiceCategories = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.CATEGORIES_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch invoice categories"); }
};

export const createInvoiceCategory = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.CATEGORIES_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create category"); }
};

export const updateInvoiceCategory = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.CATEGORY_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update category"); }
};

export const deleteInvoiceCategory = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.CATEGORY_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete category"); }
};

// ── Vendors ───────────────────────────────────────────────────────────────
export const getInvoiceVendors = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.VENDORS_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch vendors"); }
};

export const createInvoiceVendor = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.VENDORS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to create vendor"); }
};

export const updateInvoiceVendor = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.VENDOR_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update vendor"); }
};

export const deleteInvoiceVendor = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.VENDOR_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete vendor"); }
};

// Optional vendor company logo: signed PUT straight to the bucket, then the
// server checks the stored object and attaches it to the vendor.
export const uploadInvoiceVendorLogo = async (id, file) => {
  const base = INVOICE_ENDPOINTS.VENDOR_BY_ID.replace(":id", id);
  try {
    const { data } = await axios.post(`${base}/logo/upload-url`, {
      fileName: file.name, contentType: file.type, size: file.size,
    });
    const { signedUrl, key } = data?.data || {};
    const put = await fetch(signedUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
    if (!put.ok) throw new Error(`Upload failed (${put.status})`);
    const res = await axios.put(`${base}/logo`, { key, filename: file.name });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to upload the logo"); }
};

export const removeInvoiceVendorLogo = async (id) => {
  try {
    const res = await axios.delete(`${INVOICE_ENDPOINTS.VENDOR_BY_ID.replace(":id", id)}/logo`);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to remove the logo"); }
};

// ── Auditors ──────────────────────────────────────────────────────────────
export const getInvoiceAuditors = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.AUDITORS_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch auditors"); }
};

export const createInvoiceAuditor = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.AUDITORS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to add auditor"); }
};

export const updateInvoiceAuditor = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.AUDITOR_BY_ID.replace(":id", id);
    const res = await axios.patch(url, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update auditor"); }
};

export const deleteInvoiceAuditor = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.AUDITOR_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to delete auditor"); }
};

// ── Payment Settings ──────────────────────────────────────────────────────
export const getInvoicePaymentSettings = async () => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.SETTINGS_BASE);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch payment settings"); }
};

export const saveInvoicePaymentSettings = async (payload) => {
  try {
    const res = await axios.put(INVOICE_ENDPOINTS.SETTINGS_BASE, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to save payment settings"); }
};

// ── Helpers ───────────────────────────────────────────────────────────────
// Canonical approval lifecycle. Pending → Approved → Paid, with Declined as a
// terminal branch off Pending.
export const INVOICE_STATUSES = [
  { value: "Pending", color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" },
  { value: "Approved", color: "#4F46E5", bg: "#EEF2FF", border: "#C7D2FE" },
  { value: "Declined", color: "#DC2626", bg: "#FEF2F2", border: "#FECDD3" },
  { value: "Paid", color: "#059669", bg: "#ECFDF5", border: "#6EE7B7" },
];

// Neutral fallback so legacy rows (Unpaid/Overdue) still render a badge.
export const STATUS_FALLBACK = { color: "#64748B", bg: "#F1F5F9", border: "#CBD5E1" };

export const statusStyle = (status) =>
  INVOICE_STATUSES.find((s) => s.value === status) || { value: status, ...STATUS_FALLBACK };

export const computeLineTotal = (hours, minutes, amountPerHour, materialCost) => {
  const h = Number.isFinite(Number(hours)) ? Number(hours) : 0;
  const m = minutes === "" || minutes == null || !Number.isFinite(Number(minutes)) ? 0 : Number(minutes);
  const r = Number.isFinite(Number(amountPerHour)) ? Number(amountPerHour) : 0;
  const mat = materialCost === "" || materialCost == null || !Number.isFinite(Number(materialCost)) ? 0 : Number(materialCost);
  // Labour counts when a rate is set and EITHER hours or minutes is entered.
  const noTime = (hours === "" || hours == null) && (minutes === "" || minutes == null);
  const labor = noTime || amountPerHour === "" || amountPerHour == null ? 0 : (h + m / 60) * r;
  return Math.round((labor + mat) * 100) / 100;
};

// ── Rate type ─────────────────────────────────────────────────────────────
// How a line is priced. Mirrors Server/src/controllers/invoice/invoice.rate-type.ts:
//   hourly → (hours + minutes/60) × amountPerHour + materials
//   flat   → flatAmount + materials — never multiplied
// Lines stored before the field existed carry no rateType and are read the
// way the server reads them: hourly when they hold both hours and a rate,
// otherwise flat.
export const RATE_TYPES = [
  { value: "hourly", label: "Hourly" },
  { value: "flat", label: "Flat" },
];

const isSet = (v) => v !== undefined && v !== null && v !== "";

export const resolveRateType = (line) => {
  const raw = String(line?.rateType || "").trim().toLowerCase();
  if (raw === "hourly" || raw === "flat") return raw;
  return isSet(line?.hours) && isSet(line?.amountPerHour) ? "hourly" : "flat";
};

// The flat charge a stored line displays: its flatAmount, or for a legacy
// amount-only line the stored subtotal less materials. Zero for an hourly line.
export const flatAmountOf = (line) => {
  if (isSet(line?.flatAmount) && Number.isFinite(Number(line.flatAmount))) {
    return Math.round(Number(line.flatAmount) * 100) / 100;
  }
  if (resolveRateType(line) !== "flat") return 0;
  const amount = Number(line?.amount);
  if (!Number.isFinite(amount)) return 0;
  return Math.round(Math.max(0, amount - (Number(line?.materialCost) || 0)) * 100) / 100;
};

export const computeFlatLineTotal = (flatAmount, materialCost) => {
  const flat = isSet(flatAmount) && Number.isFinite(Number(flatAmount)) ? Number(flatAmount) : 0;
  const mat = isSet(materialCost) && Number.isFinite(Number(materialCost)) ? Number(materialCost) : 0;
  return Math.round((flat + mat) * 100) / 100;
};

// Pre-GST subtotal of a form row under its rate type.
export const computeRowSubtotal = (row, rateType = row?.rateType) =>
  (rateType || "hourly") === "flat"
    ? computeFlatLineTotal(row?.flatAmount, row?.materialCost)
    : computeLineTotal(row?.hours, row?.minutes, row?.amountPerHour, row?.materialCost);

// GST breakdown for a single property line.
export const computeLineMoney = (amount, gstRate) => {
  const subtotal = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  const rate = Number.isFinite(Number(gstRate)) ? Number(gstRate) : 5;
  const gstAmount = Math.round(subtotal * rate) / 100;
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    gstRate: rate,
    gstAmount: Math.round(gstAmount * 100) / 100,
    total: Math.round((subtotal + gstAmount) * 100) / 100,
  };
};

// Mirrors canEditInvoiceInStatus on the server. Kept as an allow-list for the
// same reason: an Approved or Paid invoice is settled, and anything unfamiliar
// (including the legacy Unpaid/Overdue values) is treated as locked. This only
// decides whether the button is offered — the server is what enforces it.
const EDITABLE_STATUSES = ["Pending", "Declined"];
const VENDOR_EDITABLE_STATUSES = ["Pending"];

export const isInvoiceEditable = (invoice, userRole) => {
  const status = invoice?.status;
  const isVendor = String(userRole || "").toLowerCase() === "vendor";
  const allowed = isVendor ? VENDOR_EDITABLE_STATUSES : EDITABLE_STATUSES;
  return allowed.includes(String(status));
};

// Mirrors canArchiveInvoice on the server (archive replaces delete): archive
// admins — the company account, auditors and lead technical staff — may archive
// any unapproved invoice; anyone else only what they created (a vendor only
// while Pending). Decides whether the archive button is offered; the server is
// what enforces it.
const ARCHIVABLE_STATUSES = ["Pending", "Declined"];
const VENDOR_ARCHIVABLE_STATUSES = ["Pending"];
// Roles that take part in the invoice module at all (server CREATE_ROLES).
const INVOICE_ARCHIVE_ROLES = ["company", "manager", "technical staff", "auditor", "vendor"];

export const isArchiveAdmin = (user) => {
  if (!user) return false;
  const role = String(user.role || "").toLowerCase();
  return role === "company" || role === "auditor" || (role === "technical staff" && !!user.lead);
};

export const isInvoiceArchivable = (invoice, user) => {
  if (!invoice || !user) return false;
  const role = String(user.role || "").toLowerCase();
  if (!INVOICE_ARCHIVE_ROLES.includes(role)) return false;
  const creatorId = invoice.createdBy && invoice.createdBy._id;
  const isCreator = !!user.id && !!creatorId && String(creatorId) === String(user.id);
  if (!isArchiveAdmin(user) && !isCreator) return false;
  const allowed = role === "vendor" ? VENDOR_ARCHIVABLE_STATUSES : ARCHIVABLE_STATUSES;
  return allowed.includes(String(invoice.status));
};

// Company-wide sequential number shown everywhere as "RD <n>".
export const formatInvoiceNo = (inv) =>
  inv && inv.invoiceNo != null && inv.invoiceNo !== "" ? `RD ${inv.invoiceNo}` : "—";

// Peek at the next RD number for create-form prefill. Quiet failure — the
// prefill is a best-effort suggestion, never a blocker.
export const getNextInvoiceNoPeek = async (propertyId) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_NEXT_NO, {
      // A vendor account has no company of its own — the property tells the
      // server whose numbering sequence to read.
      params: propertyId ? { propertyId } : undefined,
    });
    // `nextNo` is null when the server cannot pin the company down yet (a vendor
    // cleared by several companies). Guard it explicitly — Number(null) is 0,
    // which would prefill the field with "RD 0".
    const n = res?.data?.data?.nextNo;
    if (n === null || n === undefined || n === "") return null;
    const parsed = Number(n);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
};

// Every line of one submission that the current user may see, plus how many
// exist in total. `isPartial` is what drives the manager's "showing 2 of 5
// items" banner — without it a manager sees a smaller total than a lead does
// for the same invoice with nothing on screen explaining why.
// Quiet failure: callers fall back to the single invoice they already hold.
export const getInvoiceGroup = async (groupId) => {
  if (!groupId) return null;
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.INVOICE_GROUP.replace(":groupId", groupId));
    return res?.data?.data || null;
  } catch {
    return null;
  }
};

// Position of an invoice line within its submission. New lines persist
// `lineIndex`; lines created before invoice batching have none and fall back to
// creation order, which is _id order. Mirrors Server invoice.ordinals.ts.
export const sortInvoiceItems = (items = []) =>
  [...items].sort((a, b) => {
    const ai = typeof a?.lineIndex === "number" ? a.lineIndex : null;
    const bi = typeof b?.lineIndex === "number" ? b.lineIndex : null;
    if (ai != null && bi != null && ai !== bi) return ai - bi;
    if (ai != null && bi == null) return -1;
    if (ai == null && bi != null) return 1;
    return String(a?._id || "").localeCompare(String(b?._id || ""));
  });

// "Item 3" — 1-based, and tied to the item rather than to the viewer's slice of
// it, so a manager who sees only items 4 and 5 sees them numbered 4 and 5.
//
// Returns null when the item's true position is unknowable here. A line created
// before batching has no lineIndex, and the only honest ordinal for it comes
// from the whole group, which the list does not hold — numbering it by its
// position in a filtered list would label the same line differently for
// different viewers and disagree with its own detail view. Callers fall back to
// the line's own RD number, which is what those invoices always showed.
export const itemLabel = (item) => {
  const ordinal =
    typeof item?.ordinal === "number"
      ? item.ordinal
      : typeof item?.lineIndex === "number"
        ? item.lineIndex
        : null;
  return ordinal == null ? null : `Item ${ordinal + 1}`;
};

export const formatCurrency = (amount, currency = "CAD") => {
  const safe = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  try {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: (currency || "CAD").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safe);
  } catch {
    return `${(currency || "CAD").toUpperCase()} ${safe.toFixed(2)}`;
  }
};

export const uploadFileToInvoice = async (invoiceId, file) => {
  const { signedUrl, key } = await requestInvoiceUploadUrl(invoiceId, file.name, file.type);
  await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  await confirmInvoiceUpload(invoiceId, key);
  return key;
};

export const requestStagingUploadUrl = async (fileName, contentType) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_UPLOAD_REQUEST, { fileName, contentType });
    return res.data.data;
  } catch (e) { handleApiError(e, "Failed to obtain upload URL"); }
};

export const getStagingSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_SIGNED_URLS, { keys });
    return res.data.data;
  } catch (e) {
    handleApiError(e, "Failed to generate signed URLs");
    return {};
  }
};

export const removeStagingObject = async (key) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.INVOICE_STAGING_REMOVE, { key });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to remove file"); }
};

export const uploadStagingFile = async (file) => {
  const { signedUrl, key } = await requestStagingUploadUrl(file.name, file.type);
  const res = await fetch(signedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });
  // fetch only rejects on a network failure, so a refused PUT (expired signature, a
  // storage-side 403) arrives as a perfectly resolved promise. Without this check the key
  // is handed back as if the file were there, and the form shows an attachment that was
  // never stored.
  if (!res.ok) {
    throw new Error(`Upload failed for ${file.name} (${res.status})`);
  }
  return key;
};

// Reads a staged invoice file with the AI and returns suggested form values.
// Never toasts — the caller (the form) decides how to surface success/failure so
// autofill stays a quiet, optional enhancement. Returns null when the AI could
// not produce a usable suggestion.
export const extractInvoiceFromFile = async (key) => {
  try {
    const res = await axios.post(
      INVOICE_ENDPOINTS.INVOICE_AI_EXTRACT,
      { key },
      { headers: { "Content-Type": "application/json" } }
    );
    return res.data?.data || null;
  } catch (e) {
    console.warn("Invoice AI extract failed", e?.response?.data || e?.message);
    return null;
  }
};

// ── Payroll calendar ─────────────────────────────────────────────────────
export const getPayrollCycles = async (params = {}) => {
  try {
    const res = await axios.get(INVOICE_ENDPOINTS.PAYROLL_BASE, { params });
    return res.data;
  } catch (e) { handleApiError(e, "Failed to fetch payroll calendar"); }
};

export const createPayrollCycle = async (payload) => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.PAYROLL_BASE, payload);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to add payroll cycle"); }
};

export const updatePayrollCycle = async (id, payload) => {
  try {
    const url = INVOICE_ENDPOINTS.PAYROLL_BY_ID.replace(":id", id);
    const res = await axios.put(url, payload);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to update payroll cycle"); }
};

export const deletePayrollCycle = async (id) => {
  try {
    const url = INVOICE_ENDPOINTS.PAYROLL_BY_ID.replace(":id", id);
    const res = await axios.delete(url);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to remove payroll cycle"); }
};

export const seedPayrollCycles = async () => {
  try {
    const res = await axios.post(INVOICE_ENDPOINTS.PAYROLL_SEED);
    return res.data;
  } catch (e) { handleApiError(e, "Failed to load the default schedule"); }
};
